import { useCallback, useEffect, useRef } from 'react';
import { currentUiZoom } from '../../../../utils/uiScale';

/**
 * 输入框自动增高 hook
 * - 默认高度：两行文字（由计算样式推导）
 * - 随内容行数增长，最高不超过 maxHeight，达到上限后内部滚动（overflow-y: auto）
 *
 * mode:
 * - 'below-list'：输入框上方有历史对话列表（最近三条，初始垂直居中）。
 *   先测量列表初始（居中）位置，将列表固定在原位置（margin-top 固定，不再随输入框增高而移动），
 *   再按基准布局下的实测值计算输入框最高值：
 *     最高值 = 基准输入高度 + 输入框顶部初始位置 - 列表底部位置 - 固定间隙 - 提示词按钮高度
 *   输入框顶部最多升到"列表底部 + 固定间隙 + 提示词按钮高度"处，列表始终完整可见、不被推挤。
 * - 'half'：无列表约束，最大高度 = 容器高度的一半
 */

const MAX_RATIO = 0.6; // below-list 模式防御性上限：输入框高度不超过容器高度的 60%

export type GrowMode = 'below-list' | 'half';

interface UseAutoGrowTextareaOptions {
  value: string;
  mode: GrowMode;
  deps?: ReadonlyArray<unknown>;
}

export function useAutoGrowTextarea({ value, mode, deps = [] }: UseAutoGrowTextareaOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const modeRef = useRef<GrowMode>(mode);
  modeRef.current = mode;

  // below-list 基准测量结果（在基准布局下测量一次，之后复用）
  const baseHRef = useRef(55.6);       // 基准（两行）输入框高度
  const anchorBottomRef = useRef(0);   // 列表最下面一条记录底部相对容器的位置（固定后不再变化）
  const wrapTopBaseRef = useRef(0);    // 输入框顶部在基准布局下的位置（相对容器）
  const gapPadRef = useRef(0);         // 列表区下内边距 + 输入区上内边距
  const promptHRef = useRef(0);        // 提示词按钮高度

  const getTextMetrics = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return { lineHeight: 20, paddingV: 14 };
    const cs = getComputedStyle(ta);
    const rawLine = cs.lineHeight;
    const lineHeight = rawLine && rawLine !== 'normal' ? parseFloat(rawLine) : parseFloat(cs.fontSize) * 1.6;
    const paddingV = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    return { lineHeight, paddingV };
  }, []);

  /**
   * 重新测量基准布局并固定列表位置（仅 below-list 模式）。
   * 测量前将输入框临时置为基准（两行）高度，使布局回到初始状态；
   * 再把列表区临时设为居中，测量列表"面板中间"位置，随后恢复并
   * 通过 margin-top 把列表固定在初始位置（此后列表不再随输入框增高而移动），
   * 最后还原输入框高度。全部在同一任务内完成，无视觉闪烁。
   */
  const rePin = useCallback(() => {
    const container = containerRef.current;
    const ta = textareaRef.current;
    if (!container || !ta || modeRef.current !== 'below-list') return;
    const inner = container.querySelector<HTMLElement>('.ai-conv-recent-inner');
    const wrap = container.querySelector<HTMLElement>('.ai-rw-input-wrap');
    const section = container.querySelector<HTMLElement>('.ai-conv-recent-section');
    if (!inner || !wrap || !section) return;

    const { lineHeight, paddingV } = getTextMetrics();
    const baseH = lineHeight * 2 + paddingV;

    const prevH = ta.style.height;
    const prevOv = ta.style.overflowY;
    ta.style.height = `${baseH}px`;
    ta.style.overflowY = 'hidden';

    // 先清除上次的固定偏移，避免其污染本次"居中位置"测量
    inner.style.marginTop = '';

    /**
     * ⚠️ 单位要看清：`getBoundingClientRect()` 给的是**视觉像素**，而
     * `offsetHeight` / computed style 里的内边距是**布局像素** —— 界面整体缩放
     * （`#root` 的 zoom，见 utils/uiScale.ts）之后两者差一个系数，混着算会让
     * 输入框的固定偏移与高度偏掉。所有 rect 差值统一换算成布局像素再参与计算。
     */
    const rectPx = (visual: number): number => visual / currentUiZoom();

    const containerRect = container.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();

    // 临时居中，测量列表在"面板中间"的位置
    const prevJustify = section.style.justifyContent;
    section.style.justifyContent = 'center';
    const innerTop = rectPx(inner.getBoundingClientRect().top - containerRect.top);
    section.style.justifyContent = prevJustify;

    const sectionPadTop = parseFloat(getComputedStyle(section).paddingTop) || 0;
    const sectionPadBottom = parseFloat(getComputedStyle(section).paddingBottom) || 0;
    const bottomSection = container.querySelector<HTMLElement>('.ai-rw-bottom-section');
    const bottomPadTop = bottomSection ? parseFloat(getComputedStyle(bottomSection).paddingTop) || 0 : 0;

    // 固定列表位置（每次重新测量都会覆盖旧值，保证始终为面板居中位置）
    inner.style.marginTop = `${innerTop - sectionPadTop}px`;

    const promptEl = container.querySelector<HTMLElement>('.ai-prompt-templates');
    const promptH = promptEl?.offsetHeight ?? 0;

    ta.style.height = prevH;
    ta.style.overflowY = prevOv;

    baseHRef.current = baseH;
    anchorBottomRef.current = innerTop + inner.offsetHeight;
    wrapTopBaseRef.current = rectPx(wrapRect.top - containerRect.top);
    gapPadRef.current = sectionPadBottom + bottomPadTop;
    promptHRef.current = promptH;
  }, [getTextMetrics]);

  const computeMaxHeight = useCallback((): number => {
    const container = containerRef.current;
    if (!container) return 0;
    const containerH = container.offsetHeight;
    let maxH: number;
    if (modeRef.current === 'below-list') {
      const anchor = anchorBottomRef.current;
      const wrapTopBase = wrapTopBaseRef.current;
      if (anchor > 0 && wrapTopBase > 0) {
        maxH = baseHRef.current + wrapTopBase - anchor - gapPadRef.current - promptHRef.current;
      } else {
        maxH = containerH * 0.5;
      }
      maxH = Math.min(maxH, containerH * MAX_RATIO);
    } else {
      maxH = containerH * 0.5;
    }
    const { lineHeight, paddingV } = getTextMetrics();
    const minH = lineHeight * 2 + paddingV;
    return Math.max(minH, maxH);
  }, [getTextMetrics]);

  const applyResize = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const maxH = computeMaxHeight();
    const { lineHeight, paddingV } = getTextMetrics();
    const minH = lineHeight * 2 + paddingV;
    ta.style.height = 'auto';
    const h = Math.max(minH, Math.min(ta.scrollHeight, maxH));
    ta.style.height = `${h}px`;
    ta.style.overflowY = ta.scrollHeight > maxH ? 'auto' : 'hidden';
  }, [computeMaxHeight, getTextMetrics]);

  // 输入内容变化：仅重新计算高度（复用基准测量结果）
  useEffect(() => {
    applyResize();
  }, [value, applyResize]);

  // 视图 / 列表 / 模式等结构变化：重新测量基准、固定列表位置，再计算高度
  useEffect(() => {
    rePin();
    applyResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, ...deps, rePin, applyResize]);

  /**
   * **窗口尺寸变化**：必须重新测量并重新计算高度。
   *
   * 两件事都变了：面板宽度变了（换行数变）、界面整体缩放的系数也变了
   * （zoom 一变，之前用视觉像素量到的基准就全都偏了）。
   * 少了这一步，窗口缩放后输入框的高度与列表固定位置会停在旧值上。
   */
  useEffect(() => {
    const onResize = () => { rePin(); applyResize(); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); };
  }, [rePin, applyResize]);

  const containerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (!node) return;
    rePin();
    applyResize();
    const ro = new ResizeObserver(() => {
      rePin();
      applyResize();
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [rePin, applyResize]);

  return { containerRef: containerCallbackRef, textareaRef };
}

